import type { TestResultRequest } from '@/api/types'
import { todayIso } from '@/lib/dates'
import { DateInput } from '@/components/DateField'
import { CellCombobox } from '@/components/CellCombobox'
import { TEST_NAMES } from '@/lib/clinicalOptions'
import { RowEditor, type RowField } from './RowEditor'

export const emptyTestResult = (): TestResultRequest => ({
  test_name: '',
  result: null,
  recorded_on: null,
})

/** Rows a doctor can scan — "CBC · Normal · 12 Sep". The app records what was
 *  written; it never interprets a value or flags it as abnormal. */
const FIELDS: RowField<TestResultRequest>[] = [
  {
    key: 'test_name',
    header: 'Test',
    width: 'w-[38%]',
    label: (i) => `Test ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <CellCombobox
        className={className}
        label={label}
        placeholder="CBC"
        options={TEST_NAMES}
        value={row.test_name}
        onKeyDown={onKeyDown}
        onChange={(next) => set({ test_name: next })}
      />
    ),
  },
  {
    key: 'result',
    header: 'Result',
    width: 'w-[34%]',
    half: true,
    label: (i) => `Result for test ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <input
        className={className}
        aria-label={label}
        placeholder="Normal"
        value={row.result ?? ''}
        onKeyDown={onKeyDown}
        onChange={(e) => set({ result: e.target.value || null })}
      />
    ),
  },
  {
    key: 'recorded_on',
    header: 'Date',
    width: 'w-[28%]',
    half: true,
    label: (i) => `Date for test ${i + 1}`,
    render: ({ row, className, label, onKeyDown, set }) => (
      <DateInput
        className={className}
        withCalendar={false}
        label={label}
        max={todayIso()}
        value={row.recorded_on ?? ''}
        onKeyDown={onKeyDown}
        onChange={(iso) => set({ recorded_on: iso || null })}
      />
    ),
  },
]

export function TestResultTable({
  items,
  onChange,
}: {
  items: TestResultRequest[]
  onChange: (items: TestResultRequest[]) => void
}) {
  return (
    <RowEditor
      noun="Test"
      regionLabel="Test results"
      fields={FIELDS}
      focusField="test_name"
      items={items}
      onChange={onChange}
      blank={emptyTestResult}
      empty="No results added yet."
    />
  )
}
